import { Icon } from '@ps/isp-web-component-library';
import { StatusIconBonusCash } from '@ps/isp-web-component-library/icons/StatusIconBonusCash';
import { StatusIconCashOut } from '@ps/isp-web-component-library/icons/StatusIconCashOut';
import { StatusIconFreeBet } from '@ps/isp-web-component-library/icons/StatusIconFreeBet';
import { StatusIconLost } from '@ps/isp-web-component-library/icons/StatusIconLost';
import { StatusIconReturned } from '@ps/isp-web-component-library/icons/StatusIconReturned';
import { StatusIconVoid } from '@ps/isp-web-component-library/icons/StatusIconVoid';
import { StatusIconWon } from '@ps/isp-web-component-library/icons/StatusIconWon';
import type { ResultType, WalletInfo } from '@ps/isp-web-services-types';
import type { TranslationTag } from '@ps/web-core-intl';
import type { PropsWithoutRef, SVGProps } from 'react';
import React from 'react';

import { useRegulatoryIntl } from '@swf/core/hooks/useRegulatoryIntl';

import {
  TXTWCL_Bonus_Cash,
  TXTWCL_Cashed_out,
  TXTWCL_Free_Bet,
  TXTWCL_Lost_Xshort_formX,
  TXTWCL_Placed,
  TXTWCL_Settled,
  TXTWCL_Void_Xshort_formX,
  TXTWCL_Won_Xshort_formX,
  TXTWCL_You_Cashed_Out_N0,
  TXTWCL_You_Won_N0
} from './SettledBetsIcon.messages.json';
import styles from './SettledBetsIcon.scss';

interface Props {
  result: ResultType;
  walletInfo: WalletInfo;
}

interface BetResultStatus {
  icon: React.ElementType<PropsWithoutRef<SVGProps<SVGSVGElement>>>;
  label: TranslationTag;
  title?: TranslationTag;
}

export const betResultMap: Partial<Record<ResultType, BetResultStatus>> = {
  WON: {
    icon: StatusIconWon,
    label: TXTWCL_Won_Xshort_formX,
    title: TXTWCL_You_Won_N0
  },
  LOST: {
    icon: StatusIconLost,
    label: TXTWCL_Lost_Xshort_formX,
    title: TXTWCL_Lost_Xshort_formX
  },
  PLACED: {
    icon: StatusIconReturned,
    label: TXTWCL_Placed
  },
  VOID: {
    icon: StatusIconVoid,
    label: TXTWCL_Void_Xshort_formX,
    title: TXTWCL_Void_Xshort_formX
  },
  SETTLED: {
    icon: StatusIconReturned,
    label: TXTWCL_Settled
  },
  CASHED_OUT: {
    icon: StatusIconCashOut,
    label: TXTWCL_Cashed_out,
    title: TXTWCL_You_Cashed_Out_N0
  }
};

const extraWalletIconMap = {
  FREE_BET: {
    icon: StatusIconFreeBet,
    label: TXTWCL_Free_Bet
  },
  BONUS_CASH: {
    icon: StatusIconBonusCash,
    label: TXTWCL_Bonus_Cash
  }
};

const SettledBetsIcon: React.FC<Props> = ({ result, walletInfo }) => {
  const icon = betResultMap[result]?.icon;
  const label = betResultMap[result]?.label;
  const isExtraWalletBet = walletInfo.hasBonusCash || walletInfo.hasFreeBets;
  const { formatMessage } = useRegulatoryIntl();
  const key = walletInfo.hasBonusCash ? 'BONUS_CASH' : 'FREE_BET';

  return (
    <span className={styles.wrapper}>
      {isExtraWalletBet ? (
        <Icon
          icon={extraWalletIconMap[key].icon}
          label={formatMessage(extraWalletIconMap[key].label)}
          size="lg"
        />
      ) : icon && label ? (
        <Icon icon={icon} label={formatMessage(label)} size="lg" />
      ) : (
        <Icon icon={StatusIconReturned} label={formatMessage(TXTWCL_Settled)} size="lg" />
      )}
    </span>
  );
};

export default SettledBetsIcon;
